import React, { useEffect, useState } from "react";
import { QrCode, Download, FileText, Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";

const TEMPLATES = [
  { id: "counter", name: "Counter Stand" },
  { id: "card", name: "Visiting Card" },
  { id: "invoice", name: "Invoice Footer" },
  { id: "table", name: "Restaurant Table Card" },
  { id: "clinic", name: "Clinic Reception" },
  { id: "sticker", name: "Service Sticker" },
];

export default function QRCodePage() {
  const [qr, setQr] = useState(null);
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState("counter");
  const [title, setTitle] = useState("Scan to Review Us on Google");
  const [subtitle, setSubtitle] = useState("Your genuine feedback helps us grow");

  const generate = async () => {
    setLoading(true);
    try { const { data } = await api.post("/qr/generate", {}); setQr(data.dataUrl); setLink(data.reviewLink); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const downloadPng = () => {
    if (!qr) return;
    const a = document.createElement("a"); a.href = qr; a.download = "review-qr.png"; a.click();
    toast.success("PNG downloaded");
  };
  const downloadPdf = async () => {
    try { await downloadFile("/qr/pdf", { title, subtitle }, "review-qr.pdf"); toast.success("PDF downloaded"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const copyLink = () => { navigator.clipboard.writeText(link); toast.success("Review link copied"); };

  return (
    <div className="space-y-6">
      <PageHeader title="QR Code" subtitle="Generate a print-ready QR code for your Google review link" icon={QrCode} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="card-surface space-y-4 p-6 lg:col-span-1">
          <div><label className="mb-1 block text-xs font-medium text-slate-600">Title</label><input className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><label className="mb-1 block text-xs font-medium text-slate-600">Subtitle</label><input className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} /></div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Design Template</label>
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => setTemplate(t.id)} className={`rounded-xl border px-3 py-2 text-xs font-medium ${template === t.id ? "border-brand-500 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-600 hover:border-brand-300"}`}>{t.name}</button>
              ))}
            </div>
          </div>
          {!link && <p className="text-xs text-amber-600">No review link set. Add it in Settings to generate a QR code.</p>}
        </div>

        <div className="card-surface flex flex-col items-center justify-center p-8 lg:col-span-2">
          {loading ? <Loader2 className="h-8 w-8 animate-spin text-brand-500" /> : qr ? (
            <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-gradient-to-b from-brand-50 to-white p-8 text-center shadow-soft">
              <p className="font-heading text-lg font-bold text-brand-700">{title}</p>
              <img src={qr} alt="QR code" className="mx-auto my-5 h-56 w-56" data-testid="qr-image" />
              <p className="text-sm text-slate-500">{subtitle}</p>
              <p className="mt-3 text-[10px] text-slate-400">Powered by AWebBee.com</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Set a review link in Settings, then generate your QR.</p>
          )}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="outline" className="rounded-xl" onClick={downloadPng} disabled={!qr} data-testid="download-png-btn"><Download className="mr-1 h-4 w-4" /> PNG</Button>
            <Button variant="outline" className="rounded-xl" onClick={downloadPdf} disabled={!link} data-testid="download-pdf-btn"><FileText className="mr-1 h-4 w-4" /> PDF</Button>
            <Button variant="outline" className="rounded-xl" onClick={copyLink} disabled={!link}><Copy className="mr-1 h-4 w-4" /> Copy Link</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
